'use client'

import { AppInsightsContext, ReactPlugin } from '@microsoft/applicationinsights-react-js'
import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import { ReactNode, useEffect, useMemo } from 'react'
import { useSetLogger } from '@/components/utils/LoggerProvider'
import { createStructuredLoggerFromAppInsights } from '@/lib/logger/client'
import { env } from '@/config/client'

export type AppInsightsProviderProps = {
  children?: ReactNode
  cloudRoleName: string
  connectionString: string
  email?: string
  upn?: string
}

export function AppInsightsProvider({
  children,
  cloudRoleName,
  connectionString,
  email,
  upn,
}: AppInsightsProviderProps) {
  const reactPlugin = useMemo(() => new ReactPlugin(), [])
  const setLogger = useSetLogger()

  useEffect(() => {
    const history = {
      url: '/',
      location: { pathname: window.history.state.url },
      listen: () => {},
      ...window.history,
    }
    const appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxPerfTracking: true,
        isBrowserLinkTrackingEnabled: true,
        extensions: [reactPlugin],
        extensionConfig: {
          [reactPlugin.identifier]: { history },
        },
      },
    })
    appInsights.loadAppInsights()
    // eslint-disable-next-line consistent-return
    appInsights.addTelemetryInitializer((item) => {
      // turn off tracking of dependencies on client
      if (item.baseType === 'RemoteDependencyData') {
        return false
      }
      if (item.tags) {
        // eslint-disable-next-line no-param-reassign
        item.tags['ai.cloud.role'] = cloudRoleName
      }
    })
    if (email && upn) {
      appInsights.setAuthenticatedUserContext(email, upn)
    }
    setLogger(
      createStructuredLoggerFromAppInsights(appInsights, { version: env.NEXT_PUBLIC_APP_VERSION })
    )
    return () => {
      appInsights.unload()
    }
  }, [cloudRoleName, connectionString, reactPlugin, email, upn, setLogger])
  return <AppInsightsContext.Provider value={reactPlugin}>{children}</AppInsightsContext.Provider>
}
